import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Typography, Autocomplete, TextField, Alert, Button, Paper,
    Avatar, Chip, Stack, InputAdornment, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getClients } from '../../../services/clientService';
import { listProvidersOverview } from '../../../services/providerService';
import {
    getProviderClients, saveProviderDoc, removeProviderDoc, currentUserName,
} from '../../../services/integrationHubService';
import ProviderGrid from './ProviderGrid';
import ProviderInfoCard from './ProviderInfoCard';
import ProviderOverview from './ProviderOverview';
import { AVATAR_BG, AVATAR_FG, initials, CONNECTED_HELP_TEXT } from './constants';

const SAVE_DEBOUNCE_MS = 1500;

// Provider-first implementation workspace. Landing grid is every provider in
// the shared dictionary; opening one shows every client wired up to it, each
// with its own notes/files/links. Storage is unchanged from the client-first
// layout — one row per (client, provider) — this page just reads and writes
// it from the other direction, so saves are still scoped to whichever
// client's card actually changed.

const Integrations = () => {
    const [providersOverview, setProvidersOverview] = useState([]);
    const [allClients, setAllClients] = useState([]);
    // Always lands on the "all providers" grid — no remembered last provider.
    const [providerId, setProviderId] = useState('');

    const [clients, setClients] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [conflict, setConflict] = useState(false);
    const [saveState, setSaveState] = useState('idle');
    const [savedAt, setSavedAt] = useState(null);
    const [hasUnsaved, setHasUnsaved] = useState(false);
    // Lives here, not inside ProviderInfoCard, so toggling it takes effect on
    // ProviderOverview's segment-drift flags immediately — no refresh needed.
    const [segmentFlagsDisabled, setSegmentFlagsDisabled] = useState(false);

    // Dirty tracking: a set of client ids whose card actually changed — only
    // those get saved when the debounce fires.
    const dirtyClientIds = useRef(new Set());
    const saveTimer = useRef(null);
    const clientsRef = useRef(clients);
    clientsRef.current = clients;

    useEffect(() => {
        listProvidersOverview().then(setProvidersOverview).catch(() => setProvidersOverview([]));
        getClients().then((d) => setAllClients(d.clients || [])).catch(() => setAllClients([]));
    }, []);

    const provider = useMemo(
        () => providersOverview.find((p) => p.id === providerId) || null,
        [providersOverview, providerId],
    );

    const load = useCallback(async (id) => {
        if (!id) return;
        dirtyClientIds.current.clear();
        setLoading(true);
        setError(null);
        setConflict(false);
        setSaveState('idle');
        setHasUnsaved(false);
        try {
            setClients(await getProviderClients(id));
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(providerId); }, [providerId, load]);

    const persist = useCallback(async () => {
        setSaveState('saving');
        try {
            const who = await currentUserName();
            let anyConflict = false;

            const ids = Array.from(dirtyClientIds.current);
            dirtyClientIds.current.clear();
            for (const cid of ids) {
                const c = clientsRef.current.find((x) => x.clientId === cid);
                if (!c) continue;
                try {
                    const res = await saveProviderDoc(cid, providerId, c, who, c.updatedOn);
                    setClients((prev) => prev.map((x) => (
                        x.clientId === cid ? { ...x, updatedOn: res.updatedOn, updatedBy: who } : x
                    )));
                } catch (e) {
                    if (e.status === 409) anyConflict = true;
                    else throw e;
                }
            }

            if (anyConflict) {
                setConflict(true);
                setSaveState('error');
            } else {
                setHasUnsaved(false);
                setSaveState('saved');
                setSavedAt(new Date());
            }
        } catch (e) {
            setError(e.message);
            setSaveState('error');
        }
    }, [providerId]);

    useEffect(() => {
        if (!providerId || conflict) return undefined;
        if (dirtyClientIds.current.size === 0) return undefined;
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(persist, SAVE_DEBOUNCE_MS);
        return () => clearTimeout(saveTimer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clients, providerId, conflict, persist]);

    const onClientChange = useCallback((changed) => {
        const current = clientsRef.current.find((c) => c.clientId === changed.clientId);
        const same = current
            && JSON.stringify(changed.schema || {}) === JSON.stringify(current.schema || {});
        if (!same) {
            dirtyClientIds.current.add(changed.clientId);
            setHasUnsaved(true);
        }
        setClients((prev) => prev.map((c) => (c.clientId === changed.clientId ? changed : c)));
    }, []);

    const onAddClient = useCallback((c) => {
        setClients((prev) => [...prev, c]);
        dirtyClientIds.current.add(c.clientId);
        setHasUnsaved(true);
    }, []);

    const saveNow = () => {
        clearTimeout(saveTimer.current);
        persist();
    };

    // Removal is immediate, not debounced — same as an attachment delete, no
    // reason to wait once you've confirmed you want it gone.
    const onRemoveClient = useCallback(async (clientId) => {
        try {
            const who = await currentUserName();
            await removeProviderDoc(clientId, providerId, who);
            setClients((prev) => prev.filter((c) => c.clientId !== clientId));
            dirtyClientIds.current.delete(clientId);
        } catch (e) {
            setError(e.message);
        }
    }, [providerId]);

    const selectProvider = (id) => setProviderId(id || '');

    const backToAllProviders = () => setProviderId('');

    const connectedCount = clients.filter((c) => c.isLive).length;

    const saveLabel = {
        saving: 'Saving…',
        saved: savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : 'Saved',
        error: 'Not saved',
    }[saveState];

    return (
        <Box sx={{ width: '100%' }}>
            {!providerId && (
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: 19, fontWeight: 700 }}>Integrations</Typography>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                        Pick a provider to see which clients have it, and each one's notes and links.
                    </Typography>
                </Box>
            )}

            {providerId && (
                <Paper variant="outlined" sx={{ borderRadius: 3, p: 1.5, mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button size="small" startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} onClick={backToAllProviders}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}>
                        All providers
                    </Button>
                    <Autocomplete
                        options={providersOverview}
                        getOptionLabel={(o) => o.name || ''}
                        value={provider}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        onChange={(_, val) => selectProvider(val?.id ?? '')}
                        sx={{ width: 300 }}
                        renderInput={(params) => (
                            <TextField {...params} label="Provider" size="small" placeholder="Search providers…"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
                                }} />
                        )}
                    />
                    <Box sx={{ flex: 1 }} />
                    {saveLabel && (
                        <Chip
                            size="small"
                            label={saveLabel}
                            color={saveState === 'error' ? 'error' : saveState === 'saved' ? 'success' : 'default'}
                            variant={saveState === 'saving' ? 'outlined' : 'filled'}
                            sx={{ fontWeight: 600 }}
                        />
                    )}
                    <Button size="small" variant="outlined" onClick={saveNow} disabled={!hasUnsaved || saveState === 'saving'}
                        sx={{ textTransform: 'none' }}>
                        Save
                    </Button>
                </Paper>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>
            )}

            {conflict && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}
                    action={<Button size="small" onClick={() => load(providerId)} sx={{ textTransform: 'none' }}>Reload</Button>}>
                    Someone else saved this provider while you were editing. Reload to pick up their version —
                    your unsaved changes on this screen will be lost.
                </Alert>
            )}

            {!providerId && <ProviderGrid providers={providersOverview} onOpen={selectProvider} />}

            {providerId && loading && (
                <Typography sx={{ fontSize: 13, color: 'text.secondary', p: 3 }}>Loading provider…</Typography>
            )}

            {providerId && !loading && (
                <>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
                        <Avatar sx={{ bgcolor: AVATAR_BG, color: AVATAR_FG, width: 48, height: 48, fontWeight: 700, fontSize: 16 }}>
                            {initials(provider?.name)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 19, fontWeight: 700, lineHeight: 1.25 }}>{provider?.name || 'Provider'}</Typography>
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                                {provider?.type && <Chip size="small" variant="outlined" label={provider.type} sx={{ height: 22, fontSize: 11.5 }} />}
                                <Chip size="small" variant="outlined" label={`${clients.length} client${clients.length === 1 ? '' : 's'}`} sx={{ height: 22, fontSize: 11.5 }} />
                                <Tooltip title={CONNECTED_HELP_TEXT} arrow placement="top">
                                    <Chip
                                        size="small" variant="outlined" color={connectedCount > 0 ? 'success' : 'default'}
                                        icon={<InfoOutlinedIcon sx={{ fontSize: 13 }} />}
                                        label={`${connectedCount} connected`} sx={{ height: 22, fontSize: 11.5 }}
                                    />
                                </Tooltip>
                            </Stack>
                        </Box>
                    </Stack>

                    <ProviderInfoCard providerId={providerId} onSegmentFlagsChange={setSegmentFlagsDisabled} />

                    <ProviderOverview
                        providerId={providerId} providerName={provider?.name || ''} clients={clients} allClients={allClients}
                        segmentFlagsDisabled={segmentFlagsDisabled}
                        onClientChange={onClientChange}
                        onAddClient={onAddClient}
                        onRemoveClient={onRemoveClient}
                    />
                </>
            )}
        </Box>
    );
};

export default Integrations;
